import React, { useEffect, useState } from "react";
import { Truck, Save, ShieldCheck, AlertTriangle } from "lucide-react";
import { Modal, Button, Input, StatusBadge, LoadingBlock, toast } from "@/components/ds";
import { api, formatApiError, type DispatchConfig, type DispatchTechnician } from "@/lib/api";

export function DispatchSettings({ onClose }: { onClose: () => void }) {
  const [config, setConfig] = useState<DispatchConfig | null>(null);
  const [techs, setTechs] = useState<DispatchTechnician[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { api.dispatchConfig().then(setConfig).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { if (config?.included) api.dispatchTechnicians().then(setTechs).catch(() => {}); }, [config]);

  const saveTech = async (t: DispatchTechnician, patch: Partial<DispatchTechnician>) => {
    setBusy(t.user_id);
    try {
      await api.dispatchTechnicianSet(t.user_id, {
        skills: (patch.skills ?? t.skills), territories: (patch.territories ?? t.territories),
        active: patch.active ?? t.active, max_daily_jobs: patch.max_daily_jobs ?? t.max_daily_jobs,
      });
      toast.success("Technician saved");
      api.dispatchTechnicians().then(setTechs).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const toggleAuto = async (v: boolean) => {
    try { const c = await api.dispatchConfigSet(v); setConfig(c); toast.success(v ? "Automated routing enabled" : "Automated routing disabled"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Smart Dispatch" description="Set technician skills and territories so ResFil can recommend the best technician for each job." testid="dispatch-settings-modal">
      <div className="max-h-[74vh] space-y-4 overflow-y-auto pr-1">
        {!config ? <LoadingBlock rows={3} /> : !config.included ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700" data-testid="dispatch-upgrade">
            Smart Dispatch recommendations are available on the Pro plan. Growth includes multi-calendar scheduling; upgrade to Pro to get recommended-technician suggestions, and Custom for automated routing.
          </div>
        ) : (
          <>
            <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="dispatch-note">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
              <p className="text-xs text-blue-800">ResFil recommends a technician and explains why — <b>staff confirm each assignment</b>. {config.auto_routing_available ? "On Custom you can enable automated routing to auto-assign the top recommendation." : "Automated routing is a Custom-plan capability."}</p>
            </div>

            {config.auto_routing_available && (
              <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm text-slate-700" data-testid="dispatch-auto-toggle">
                <input type="checkbox" checked={config.auto_assign} onChange={(e) => toggleAuto(e.target.checked)} data-testid="dispatch-auto-checkbox" />
                Enable automated routing — auto-assign the top recommendation when an appointment is opened.
              </label>
            )}

            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-800">Technicians</h3>
              {techs === null ? <LoadingBlock rows={2} /> : techs.length === 0 ? (
                <p className="text-sm text-slate-500">No team members yet. Invite staff under Team to dispatch them.</p>
              ) : (
                <div className="space-y-3" data-testid="dispatch-technicians">
                  {techs.map((t) => (
                    <TechRow key={t.user_id} tech={t} busy={busy === t.user_id} onSave={saveTech} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <div className="mt-4 flex justify-end border-t border-slate-100 pt-4">
        <Button variant="outline" onClick={onClose} data-testid="dispatch-close-btn">Done</Button>
      </div>
    </Modal>
  );
}

function TechRow({ tech, busy, onSave }: { tech: DispatchTechnician; busy: boolean; onSave: (t: DispatchTechnician, p: Partial<DispatchTechnician>) => void }) {
  const [skills, setSkills] = useState(tech.skills.join(", "));
  const [territories, setTerritories] = useState(tech.territories.join(", "));
  const [maxJobs, setMaxJobs] = useState(tech.max_daily_jobs != null ? String(tech.max_daily_jobs) : "");
  // Default new technicians (no saved profile yet) to active so a first save isn't silently excluded.
  const [active, setActive] = useState(tech.has_profile ? tech.active : true);

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`dispatch-tech-${tech.user_id}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-800">{tech.name}</span>
          {tech.role && <StatusBadge tone="slate">{tech.role}</StatusBadge>}
          {active ? <StatusBadge tone="green">Dispatchable</StatusBadge> : <StatusBadge tone="amber">Off — won't be recommended</StatusBadge>}
        </div>
        <label className="flex items-center gap-1 text-xs text-slate-600">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid={`dispatch-tech-active-${tech.user_id}`} /> Active
        </label>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <div><p className="mb-1 text-[11px] font-medium text-slate-500">Skills (comma-separated)</p>
          <Input value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="AC repair, Heating" className="text-xs" data-testid={`dispatch-tech-skills-${tech.user_id}`} /></div>
        <div><p className="mb-1 text-[11px] font-medium text-slate-500">Territories (zips / cities)</p>
          <Input value={territories} onChange={(e) => setTerritories(e.target.value)} placeholder="75001, Dallas" className="text-xs" data-testid={`dispatch-tech-territories-${tech.user_id}`} /></div>
        <div><p className="mb-1 text-[11px] font-medium text-slate-500">Max jobs/day</p>
          <Input type="number" min="0" value={maxJobs} onChange={(e) => setMaxJobs(e.target.value)} className="text-xs" data-testid={`dispatch-tech-max-${tech.user_id}`} /></div>
      </div>
      <div className="mt-2 flex justify-end">
        <Button size="sm" disabled={busy} data-testid={`dispatch-tech-save-${tech.user_id}`}
          onClick={() => onSave(tech, { skills: skills.split(",").map((s) => s.trim()).filter(Boolean), territories: territories.split(",").map((s) => s.trim()).filter(Boolean), active, max_daily_jobs: maxJobs === "" ? null : Number(maxJobs) })}>
          <Save className="h-3.5 w-3.5" /> Save
        </Button>
      </div>
    </div>
  );
}

export function DispatchPanel({ apptId, assignedId, onChanged }: { apptId: string; assignedId?: string | null; onChanged: () => void }) {
  const [data, setData] = useState<import("@/lib/api").DispatchRecommendations | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "unavailable">("idle");

  const load = () => {
    setState("loading");
    api.dispatchRecommendations(apptId)
      .then((d) => { setData(d); setState("idle"); })
      .catch((e) => { setState(formatApiError(e).toLowerCase().includes("pro plan") ? "unavailable" : "idle"); });
  };

  const assign = async (tid: string) => {
    try { await api.dispatchAssign(apptId, tid); toast.success("Technician assigned"); onChanged(); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (state === "unavailable") return null;

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="dispatch-panel">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Truck className="h-4 w-4 text-blue-600" /> Smart Dispatch</h4>
        <Button size="sm" variant="outline" onClick={load} data-testid="dispatch-recommend-btn">Get recommendation</Button>
      </div>
      {assignedId && data?.recommendations && (
        <p className="mb-2 text-xs text-emerald-700" data-testid="dispatch-assigned">Assigned: <b>{data.recommendations.find((r) => r.technician_id === assignedId)?.name || assignedId}</b>{data.auto_assign ? " (auto-assigned)" : ""}</p>
      )}
      {state === "loading" && <LoadingBlock rows={2} />}
      {data && (
        data.recommendations.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="dispatch-no-techs">No dispatchable technicians. Add skills/territories under Scheduling → Dispatch.</p>
        ) : (
          <div className="space-y-2" data-testid="dispatch-recommendations">
            {data.recommendations.map((r, i) => (
              <div key={r.technician_id} className={`rounded-lg border p-2 ${i === 0 && r.eligible ? "border-blue-300 bg-blue-50/50" : "border-slate-200"}`} data-testid={`dispatch-rec-${r.technician_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-800">{r.name}</span>
                    {i === 0 && r.eligible && <StatusBadge tone="blue">Recommended</StatusBadge>}
                    {!r.available && <StatusBadge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> Conflict</StatusBadge>}
                    {r.over_capacity && <StatusBadge tone="amber">At capacity</StatusBadge>}
                  </div>
                  <Button size="sm" disabled={assignedId === r.technician_id} onClick={() => assign(r.technician_id)} data-testid={`dispatch-assign-${r.technician_id}`}>
                    {assignedId === r.technician_id ? "Assigned" : "Assign"}
                  </Button>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">{r.reasons.join(" · ")}</p>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
